/**
 * plugins/icons/entities.js
 *
 * The single registry of entity glyphs, and the only place allowed to decide
 * which glyph an entity gets. It mirrors `docs/icons.md`, which stays the
 * written standard — this file is that standard as code.
 *
 * Import from `@/plugins/icons`, never from a component's own module:
 *
 *     import { ICONS, levelIcon } from '@/plugins/icons'
 *
 * Two rules keep it useful:
 *
 *   1. A component never writes a glyph name for an *entity*. It reads one from
 *      here. Generic action glyphs (`mdi-plus`, `mdi-close`, `mdi-check`, the
 *      chevrons) stay inline — they are not entities and listing them would bury
 *      the ones that matter.
 *   2. When an entity's glyph depends on its state or its type, the decision
 *      lives here as a function, not as a ternary at the call site.
 *
 * `pit:` names come from our own set (`./pit.js`, icons.md §14). Everything else
 * is MDI.
 */

export const ICONS = {
    // §1 Navigation
    company: 'mdi-factory',
    project: 'mdi-home-modern',
    superproject: 'mdi-city-variant-outline',
    standard_library: 'mdi-library-outline',
    client: 'mdi-domain',
    resources: 'mdi-newspaper-variant-outline',
    viewer: 'mdi-map-legend',
    manage: 'mdi-laptop',
    production: 'pit:production',
    production_settings: 'mdi-window-shutter-cog',
    logistics: 'mdi-forklift',
    dashboard: 'mdi-view-dashboard',
    planning: 'mdi-chart-gantt',
    files: 'mdi-file-document-outline',

    // §1 Issues — one glyph per state
    issue: 'mdi-comment-outline',
    issue_alert: 'mdi-comment-alert-outline',
    issue_editing: 'mdi-comment-edit-outline',
    issue_resolved: 'mdi-comment-check-outline',

    // §2 Constructibles
    module: 'pit:module',
    assembly: 'pit:assembly',
    part: 'mdi-lightbulb-fluorescent-tube-outline',
    zones: 'mdi-layers-triple-outline',
    zone: 'mdi-layers',
    multipanel_group: 'mdi-set-merge',

    // §3 Designs — a design reuses its constructible's glyph
    design: 'mdi-ruler-square-compass',
    module_design: 'pit:module',
    assembly_design: 'pit:assembly',
    part_design: 'mdi-lightbulb-fluorescent-tube',

    // §4 Factory
    production_line: 'mdi-transit-connection-variant',
    production_station: 'mdi-table-furniture',
    manual_station: 'mdi-hammer-screwdriver',
    machine_station: 'mdi-robot-industrial',
    machine: 'mdi-robot-industrial-outline',
    machine_settings: 'mdi-robot-industrial-outline',
    automations: 'mdi-robot-outline',
    robobrick: 'mdi-wall',
    action_trigger: 'mdi-flash-outline',
    action_setup: 'mdi-cog-play-outline',
    checklist_item: 'mdi-format-list-checks',
    rack: 'mdi-bookshelf',
    rack_trace: 'mdi-map-marker-path',

    // §5 Planning & production
    phase: 'mdi-timeline-outline',
    phase_template: 'mdi-timeline-outline',
    batch: 'pit:batch',
    batch_full: 'pit:batch-full',
    part_batch: 'mdi-tray',
    part_batch_full: 'mdi-tray-full',
    rcp: 'mdi-check-decagram-outline',
    production_event: 'mdi-timeline-text-outline',
    forecast: 'mdi-chart-bell-curve-cumulative',

    // §6 Logistics & catalogue
    product: 'mdi-texture-box',
    material: 'mdi-palette-swatch-outline',
    size: 'mdi-tape-measure',
    stock_level: 'mdi-package-variant',
    order: 'mdi-clipboard-list-outline',
    supplier: 'mdi-store-outline',
    delivery: 'mdi-truck-delivery-outline',

    // §7 Documents & files
    production_files: 'mdi-saw-blade',
    management_files: 'mdi-file-document-outline',
    file_settings: 'mdi-file-settings-outline',
    machine_file: 'mdi-file-cog',
    generation_template: 'mdi-file-cog-outline',
    report: 'mdi-file-table-outline',
    linked_file_package: 'mdi-folder-zip-outline',
    qr: 'mdi-qrcode',
    qr_scanner: 'mdi-qrcode-scan',

    // §8 People & access
    user: 'mdi-account-outline',
    operator: 'mdi-account-hard-hat',
    contact: 'mdi-card-account-details-outline',
    authorization: 'mdi-shield-account-outline',

    // §9 System & integrations
    ifcps: 'mdi-import',
    webhook: 'mdi-webhook',
    api_account: 'mdi-api',
    external_app: 'mdi-apps',
    work_calendar: 'mdi-calendar-clock',
    background_job: 'mdi-progress-clock',
    message: 'mdi-email-outline',

    // §11 Reserved non-entity glyphs
    section_rectangular: 'mdi-rectangle-outline',
    section_shaped: 'mdi-vector-union',
    branch_timber: 'pit:timber',
    branch_plate: 'pit:plate',
    branch_custom: 'mdi-source-branch',
    batch_times: 'mdi-clipboard-text-clock',
    on_time_metric: 'mdi-clock-check-outline',
    glb: 'mdi-cube-outline',
    unknown: 'mdi-help',
}

/* ------------------------------------------------------------------ *
 * Resolvers — an entity whose glyph depends on state or on type.
 * ------------------------------------------------------------------ */

/**
 * Constructible level, for a constructible *and* its design alike (§3).
 * `level` is the field the API uses everywhere: "module" or "assembly".
 * Anything else is an assembly, which is what every other level is.
 */
export function levelIcon(level) {
    return level === 'module' ? ICONS.module : ICONS.assembly
}

/**
 * Part branch (§11). A branch is a family of shapes, so the glyph says what the
 * geometry is: timber is an extruded solid, plate is a sheet. Only the two
 * built-in branches have a fixed geometry; a branch declared in a parsing setup
 * is whatever its `alike` says, so it keeps the generic branch glyph.
 */
export function branchIcon(branch) {
    if (branch === 'timber') return ICONS.branch_timber
    if (branch === 'plate') return ICONS.branch_plate
    return ICONS.branch_custom
}

/**
 * A constructible `tipo`. Module and assembly tipos carry their entity glyph;
 * a part tipo *is* a branch, so timber and plate get their geometry and the
 * rest fall back to the generic Part glyph.
 *
 * The tipo lists live in `components/fields/tipos.js` and are passed in, so
 * this module stays free of imports and can be read on its own.
 */
export function tipoIcon(tipo, { module_tipos = [], assembly_tipos = [], part_tipos = [] } = {}) {
    if (module_tipos.includes(tipo)) return ICONS.module
    if (assembly_tipos.includes(tipo)) return ICONS.assembly
    if (tipo === 'timber') return ICONS.branch_timber
    if (tipo === 'plate') return ICONS.branch_plate
    if (part_tipos.includes(tipo)) return ICONS.part
    return ICONS.unknown
}

/**
 * Batch, filled once it is finished.
 *
 * `status` is **derived, not stored**: `Batch` has no status column, the API
 * computes it from the batch's constructibles and a batch is `ready` only when
 * every assembly and module in it is. A batch holding nothing reports
 * `waiting`, not `ready`.
 *
 * So anything calling this needs the batch as the API sent it. A batch mapped
 * down to `{ title, value }` for a select has lost its status.
 */
export function batchIcon(batch) {
    return batch?.status === 'ready' ? ICONS.batch_full : ICONS.batch
}

/**
 * PartBatch, filled once the batch is `ready`. The tray family belongs to
 * PartBatch alone — Batch used to share it and now has its own glyph (§5).
 */
export function partBatchIcon(batch) {
    return batch?.status === 'ready' ? ICONS.part_batch_full : ICONS.part_batch
}

/**
 * The status *chip* of a part batch — the session state, not the entity.
 * Paused beats working: a paused batch is not progressing.
 */
export function partBatchStatusIcon(batch) {
    if (!batch) return 'mdi-clock-outline'
    if (batch.status === 'ready') return 'mdi-check'
    if (batch.paused) return 'mdi-pause'
    if (batch.status === 'working') return 'mdi-play'
    return 'mdi-clock-outline'
}
